import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Search, ShieldBan, Undo2, PhoneOff } from 'lucide-react'
import { toast } from 'sonner'
import {
  useOptOutSearch,
  useOptOutList,
  useOptOutContact,
  useRestoreContact,
} from '@/hooks/useWAOptouts'
import type { WAOptOutSearchResult } from '@/lib/api-client'

export const Route = createFileRoute('/whatsapp-optouts')({
  component: WhatsAppOptoutsPage,
})

const DEFAULT_REASON = 'Complaint after blast'
const REASONS = [DEFAULT_REASON, 'Not interested', 'Moved away', 'Wrong number', 'Other']

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function maskNric(nric: string | null): string {
  if (!nric || nric.length < 8) return '—'
  return `${nric.slice(0, 6)}-**-${nric.slice(-2)}`
}

interface BlockTarget {
  contactId?: number
  patientId?: number
  phoneNumber?: string
  label: string
  phone: string
}

function WhatsAppOptoutsPage() {
  const [searchInput, setSearchInput] = React.useState('')
  const [query, setQuery] = React.useState('')
  const [blockTarget, setBlockTarget] = React.useState<BlockTarget | null>(null)
  const [reason, setReason] = React.useState(DEFAULT_REASON)
  const [notes, setNotes] = React.useState('')

  // Debounce search input
  React.useEffect(() => {
    const t = setTimeout(() => setQuery(searchInput), 350)
    return () => clearTimeout(t)
  }, [searchInput])

  const search = useOptOutSearch(query)
  const optoutList = useOptOutList()
  const optOut = useOptOutContact()
  const restore = useRestoreContact()

  const results = search.data?.data ?? []
  const normalizedPhone = search.data?.normalizedPhone ?? null
  const listItems = optoutList.data?.data ?? []

  // Group patients sharing the same contact so staff see shared numbers
  const sharedCounts = React.useMemo(() => {
    const counts = new Map<number, number>()
    for (const r of results) {
      if (r.contactId) counts.set(r.contactId, (counts.get(r.contactId) ?? 0) + 1)
    }
    return counts
  }, [results])

  const openBlockDialog = (target: BlockTarget) => {
    setReason(DEFAULT_REASON)
    setNotes('')
    setBlockTarget(target)
  }

  const handleBlock = () => {
    if (!blockTarget) return
    optOut.mutate(
      {
        contactId: blockTarget.contactId,
        patientId: blockTarget.contactId ? undefined : blockTarget.patientId,
        phoneNumber:
          blockTarget.contactId || blockTarget.patientId ? undefined : blockTarget.phoneNumber,
        reason,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success(`${blockTarget.label} added to Do Not Blast list`)
          setBlockTarget(null)
        },
        onError: (err) => toast.error(err.message),
      }
    )
  }

  const handleRestore = (contactId: number, label: string) => {
    restore.mutate(
      { contactId },
      {
        onSuccess: () => toast.success(`${label} restored — can receive broadcasts again`),
        onError: (err) => toast.error(err.message),
      }
    )
  }

  const showNoMatch =
    query.trim().length >= 2 && !search.isPending && results.length === 0
  const canBlockDirectly = showNoMatch && normalizedPhone

  return (
    <div className="flex flex-col h-full">
      <Header
        title="Do Not Blast"
        subtitle={
          optoutList.data
            ? `${optoutList.data.total} contacts excluded from all broadcasts`
            : 'Manage broadcast exclusions'
        }
      />

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-9 pb-8 space-y-8">
        {/* Search & Add section */}
        <section className="bg-card border border-border rounded-2xl p-5">
          <h2 className="text-[15px] font-semibold text-text-dark mb-1">Add to list</h2>
          <p className="text-[13px] text-text-secondary mb-4">
            Search by patient name, phone number, NRIC, or patient no (PNO). Blocked contacts are
            skipped by every campaign automatically.
          </p>

          <div className="relative max-w-xl">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="e.g. NORHANIZA, 0199659744, 910904065468, 000100"
              className="pl-10 rounded-full"
            />
          </div>

          {search.isPending && query.trim().length >= 2 && (
            <div className="mt-4 space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {results.length > 0 && (
            <div className="mt-4 border border-border rounded-xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>PNO</TableHead>
                    <TableHead>NRIC</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((r, i) => (
                    <SearchResultRow
                      key={`${r.patientId ?? 'c'}-${r.contactId ?? i}`}
                      result={r}
                      sharedCount={r.contactId ? (sharedCounts.get(r.contactId) ?? 1) : 1}
                      onBlock={openBlockDialog}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {showNoMatch && (
            <div className="mt-4 flex items-center gap-3 rounded-xl border border-dashed border-border px-4 py-3">
              <PhoneOff className="w-4 h-4 text-text-muted flex-shrink-0" />
              {canBlockDirectly ? (
                <>
                  <div className="flex-1 text-[13px] text-text-secondary">
                    No patient or contact found for this number. You can still block{' '}
                    <span className="font-medium text-text-dark">{normalizedPhone}</span> directly
                    (e.g. a lead, or a patient who changed number).
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() =>
                      openBlockDialog({
                        phoneNumber: normalizedPhone,
                        label: normalizedPhone,
                        phone: normalizedPhone,
                      })
                    }
                  >
                    <ShieldBan className="w-3.5 h-3.5 mr-1.5" />
                    Block number
                  </Button>
                </>
              ) : (
                <div className="text-[13px] text-text-secondary">
                  No match found. Try a different spelling, or paste the phone number to block it
                  directly.
                </div>
              )}
            </div>
          )}
        </section>

        {/* Current list section */}
        <section className="bg-card border border-border rounded-2xl p-5">
          <h2 className="text-[15px] font-semibold text-text-dark mb-4">
            Currently blocked{' '}
            {optoutList.data && (
              <Badge variant="secondary" className="ml-1">
                {optoutList.data.total}
              </Badge>
            )}
          </h2>

          {optoutList.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : listItems.length === 0 ? (
            <p className="text-[13px] text-text-secondary">No blocked contacts yet.</p>
          ) : (
            <div className="border border-border rounded-xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient / Contact</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Blocked on</TableHead>
                    <TableHead className="max-w-[320px]">Reason</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listItems.map((item) => {
                    const label = item.patientName || item.displayName || item.waPhoneNumber
                    return (
                      <TableRow key={item.contactId}>
                        <TableCell>
                          <div className="font-medium text-text-dark">{label}</div>
                          {item.patientName && item.displayName && (
                            <div className="text-[12px] text-text-muted">
                              WA: {item.displayName}
                            </div>
                          )}
                          {!item.patientId && (
                            <Badge variant="outline" className="mt-0.5 text-[11px]">
                              Not a patient
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-[13px]">
                          {item.waPhoneNumber}
                        </TableCell>
                        <TableCell>{item.branchCode ?? '—'}</TableCell>
                        <TableCell>{formatDate(item.optOutDate)}</TableCell>
                        <TableCell className="max-w-[320px]">
                          <div className="text-[12px] text-text-secondary line-clamp-2">
                            {item.reason ?? '—'}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={restore.isPending}
                            onClick={() => handleRestore(item.contactId, label)}
                          >
                            <Undo2 className="w-3.5 h-3.5 mr-1.5" />
                            Restore
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </div>

      {/* Block confirmation dialog */}
      <Dialog open={!!blockTarget} onOpenChange={(open) => !open && setBlockTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block from broadcasts</DialogTitle>
            <DialogDescription>
              <span className="font-medium text-foreground">{blockTarget?.label}</span> (
              {blockTarget?.phone}) will be permanently excluded from all campaigns until restored.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-[13px] font-medium text-text-dark mb-1.5 block">Reason</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[13px] font-medium text-text-dark mb-1.5 block">
                Notes (optional)
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. reported by clinic staff via WhatsApp"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBlock} disabled={optOut.isPending}>
              <ShieldBan className="w-4 h-4 mr-1.5" />
              {optOut.isPending ? 'Blocking...' : 'Block'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SearchResultRow({
  result: r,
  sharedCount,
  onBlock,
}: {
  result: WAOptOutSearchResult
  sharedCount: number
  onBlock: (target: BlockTarget) => void
}) {
  const label = r.patientName || r.displayName || r.waPhoneNumber || 'Unknown'
  const phone = r.waPhoneNumber || r.patientPhone || '—'
  const isBlocked = r.optInStatus === 'opted_out'

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium text-text-dark">{label}</div>
        {r.patientName && r.displayName && (
          <div className="text-[12px] text-text-muted">WA: {r.displayName}</div>
        )}
        {!r.patientId && (
          <Badge variant="outline" className="mt-0.5 text-[11px]">
            Not a patient
          </Badge>
        )}
        {sharedCount > 1 && (
          <Badge variant="secondary" className="mt-0.5 ml-1 text-[11px]">
            Number shared by {sharedCount} patients
          </Badge>
        )}
      </TableCell>
      <TableCell className="font-mono text-[13px]">{phone}</TableCell>
      <TableCell>{r.pno ?? '—'}</TableCell>
      <TableCell className="font-mono text-[12px]">{maskNric(r.nric)}</TableCell>
      <TableCell>{r.branchCode ?? '—'}</TableCell>
      <TableCell>
        {isBlocked ? (
          <Badge variant="destructive">Blocked</Badge>
        ) : r.optInStatus ? (
          <Badge variant="secondary" className="capitalize">
            {r.optInStatus.replace('_', ' ')}
          </Badge>
        ) : (
          <Badge variant="outline">No WA contact yet</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        {isBlocked ? (
          <span className="text-[12px] text-text-muted">Already blocked</span>
        ) : (
          <Button
            size="sm"
            variant="destructive"
            onClick={() =>
              onBlock({
                contactId: r.contactId ?? undefined,
                patientId: r.patientId ?? undefined,
                label,
                phone,
              })
            }
          >
            <ShieldBan className="w-3.5 h-3.5 mr-1.5" />
            Block
          </Button>
        )}
      </TableCell>
    </TableRow>
  )
}
